import {
  RAMP1_MEDS_RETAIL_PRICE,
  RAMP1_MEDS_SALE_PRICE,
  RAMP2_MEDS_SALE_PRICE,
  RAMP1_MEDS_SUBLABEL,
  SALES_PITCH,
  CONSULT_FEE_LABEL,
  CONSULT_FEE_SUBLABEL,
  CLINICAL_ADDON_CODE,
  TOTAL_SUBLABEL,
  SUBSCRIPTION_TYPE,
  OWNER_ID,
  WW_OWNER_ID
} from "../../../constants";

/**
 * @typedef {object} Addon
 * @property {object} addonItem
 * @property {string} addonItem.id
 * @property {string} addonItem.name
 * @property {string} addonItem.baseItem.code
 * @property {string} retailPrice
 * @property {string} salePrice
 * @property {string} id
 * @property {string} name
 */
/**
 * @typedef {object} Plan
 * @property {string} id
 * @property {string} name
 * @property {string} ltc
 * @property {string} salePrice
 * @property {string} retailPrice
 * @property {string} subtotal
 * @property {Map<string, string>} contentMap
 *
 */
/**
 * @typedef {object} NormalizedPlan
 * @property {string} id
 * @property {string} name
 * @property {string} ltc
 * @property {string} salePrice
 * @property {string} retailPrice
 * @property {string} subtotal
 * @property {Map<string, string>} contentMap
 * @property {string} salesPitch
 * @property {string} formattedSalePrice
 * @property {boolean} showCrossoutPrice
 * @property {string} ramp1RetailPrice
 * @property {string} salePrice
 * @property {object} clinical
 * @property {boolean} clinical.hasClinicalAddon
 * @property {[string]} clinical.label
 * @property {[string]} clinical.sublabel
 * @property {[boolean]} clinical.showCrossoutPrice
 * @property {[string]} clinical.retailPrice
 * @property {[string]} clinical.salePrice
 * @property {[number]} clinical.unformattedSalePrice
 *
 */

/**
 *
 * @param {object} props
 * @param {Plan} props.plan
 * @param {string} props.locale
 * @returns {NormalizedPlan}
 */
const normalizePlan = ({ plan, locale, isCompound = false }) => {
  const {
    contentMap = new Map(),
    currency = "USD",
    plan: { basePlan, freeTrialDays }
  } = plan;
  const salesPitch =
    contentMap.get(RAMP1_MEDS_SUBLABEL) || contentMap.get(SALES_PITCH);
  const [ramp1RetailPrice] =
    getNumericString(contentMap.get(RAMP1_MEDS_RETAIL_PRICE)) || [];
  const [totalSubLabel] =
    getNumericString(contentMap.get(TOTAL_SUBLABEL)) || [];
  const firstMonthSalePrice = contentMap.get(RAMP1_MEDS_SALE_PRICE);

  const totalBillingCycles = basePlan.totalBillingCycles;
  const isFreeTrial = freeTrialDays > 0;
  const intervalLength = basePlan.intervalLength;
  const normalizedCustomFields = basePlan.customFields.reduce((acc, field) => {
    acc.set(field.name, field.value);
    return acc;
  }, new Map());

  const subscriptionType = normalizedCustomFields.get(SUBSCRIPTION_TYPE);
  const isFranchise = normalizedCustomFields.get(OWNER_ID) !== WW_OWNER_ID;
  const basePlanName = basePlan?.name;

  const salePrice = isCompound
    ? firstMonthSalePrice || totalSubLabel
    : totalSubLabel;
  const showCrossoutPrice = ramp1RetailPrice && ramp1RetailPrice !== salePrice;
  const ramp2SalePrice = contentMap.get(RAMP2_MEDS_SALE_PRICE);
  const formattedSalePrice = formatCurrency({
    num: salePrice,
    currency,
    locale
  });
  const formattedRamp2SalePrice = formatCurrency({
    num: ramp2SalePrice,
    currency,
    locale
  });
  const formattedTotal = formatCurrency({
    num: plan.total,
    currency,
    locale
  });

  let formattedRetailPrice = null;
  if (showCrossoutPrice) {
    formattedRetailPrice = formatCurrency({
      num: ramp1RetailPrice,
      currency,
      locale
    });
  }

  const clinicalAddon = plan.addons.find(
    addon => addon?.addonItem?.baseItem.code === CLINICAL_ADDON_CODE
  );

  const clinical = {
    hasClinicalAddon: !!clinicalAddon,
    label: contentMap.get(CONSULT_FEE_LABEL),
    sublabel: contentMap.get(CONSULT_FEE_SUBLABEL)
  };

  if (clinicalAddon) {
    clinical.showCrossoutPrice =
      clinicalAddon.retailPrice &&
      clinicalAddon.retailPrice !== clinicalAddon.salePrice;
    clinical.retailPrice = formatCurrency({
      num: clinicalAddon.retailPrice,
      currency,
      locale
    });
    clinical.unformattedSalePrice = clinicalAddon.salePrice;
    clinical.salePrice = formatCurrency({
      num: clinicalAddon.salePrice,
      currency,
      locale
    });
  }

  return {
    ...plan,
    basePlanName,
    subscriptionType,
    intervalLength,
    totalBillingCycles,
    isFranchise,
    isFreeTrial,
    clinical,
    formattedSalePrice,
    formattedRamp2SalePrice,
    formattedRetailPrice,
    formattedTotal,
    salesPitch,
    salePrice,
    ramp2SalePrice,
    showCrossoutPrice,
    ramp1RetailPrice
  };
};

export default normalizePlan;

/**
 *
 * @param {object} param0
 * @param {number} param0.num
 * @param {string} param0.currency
 * @param {string} param0.locale
 * @returns
 */
function formatCurrency({ num, currency, locale }) {
  return Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 0
  }).format(Number(num));
}

function getNumericString(str) {
  return str?.match(/[0-9.]+/g);
}
